"use client"

import { useState, useEffect, useCallback } from 'react'

export interface SavedProfile {
  id: string
  name: string
  title: string
  location: string
  imageUrl?: string
  savedAt: string
}

const STORAGE_KEY = 'boardchampions_saved_profiles'

export function useSavedProfiles() {
  const [savedProfiles, setSavedProfiles] = useState<SavedProfile[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Load saved profiles from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        setSavedProfiles(JSON.parse(stored))
      }
    } catch (error) {
      console.error('Error loading saved profiles:', error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Save profiles to localStorage whenever they change
  useEffect(() => {
    if (!isLoading) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(savedProfiles))
        // Dispatch custom event for same-tab updates
        window.dispatchEvent(new Event('savedProfilesUpdated'))
      } catch (error) {
        console.error('Error saving profiles to localStorage:', error)
      }
    }
  }, [savedProfiles, isLoading])

  // Check if a profile is saved
  const isProfileSaved = useCallback((profileId: string) => {
    return savedProfiles.some(p => p.id === profileId)
  }, [savedProfiles])

  // Toggle save status for a profile
  const toggleSaveProfile = useCallback((profile: Omit<SavedProfile, 'savedAt'>) => {
    setSavedProfiles(prev => {
      const isSaved = prev.some(p => p.id === profile.id)

      if (isSaved) {
        // Remove from saved
        return prev.filter(p => p.id !== profile.id)
      } else {
        // Add to saved
        return [...prev, {
          ...profile,
          savedAt: new Date().toISOString()
        }]
      }
    })
  }, [])

  // Remove a profile from saved
  const removeProfile = useCallback((profileId: string) => {
    setSavedProfiles(prev => prev.filter(p => p.id !== profileId))
  }, [])

  // Clear all saved profiles
  const clearAllProfiles = useCallback(() => {
    setSavedProfiles([])
    localStorage.removeItem(STORAGE_KEY)
  }, [])

  // Get count of saved profiles
  const getSavedCount = useCallback(() => {
    return savedProfiles.length
  }, [savedProfiles])

  return {
    savedProfiles,
    isLoading,
    isProfileSaved,
    toggleSaveProfile,
    removeProfile,
    clearAllProfiles,
    getSavedCount
  }
}